
import React from 'react';
import { ParticleAnimation } from './ParticleAnimation';

interface EnergyMeter3DProps {
  score: number;
  size?: number;
}

export const EnergyMeter3D: React.FC<EnergyMeter3DProps> = ({ score, size = 200 }) => {
  const circumference = 2 * Math.PI * 40;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'from-emerald-500 to-emerald-700';
    if (score >= 60) return 'from-amber-500 to-orange-600';
    return 'from-orange-500 to-red-600';
  };

  const getShadowColor = (score: number) => {
    if (score >= 80) return 'shadow-emerald-200';
    if (score >= 60) return 'shadow-amber-200';
    return 'shadow-orange-200';
  };

  return (
    <div className="relative" style={{ width: size, height: size }}>
      {/* 3D Base Container with subtle shadow */}
      <div className={`relative w-full h-full rounded-full bg-gradient-to-br from-gray-50 to-gray-100 shadow-lg ${getShadowColor(score)}`}>
        
        {/* Inner highlight effect */}
        <div className="absolute inset-2 rounded-full bg-gradient-to-br from-white/40 to-transparent"></div>
        
        {/* SVG Meter */}
        <svg 
          className="absolute inset-0 w-full h-full transform -rotate-90" 
          viewBox="0 0 100 100"
        >
          {/* Background circle */}
          <circle
            cx="50"
            cy="50"
            r="40"
            stroke="#e5e7eb"
            strokeWidth="6"
            fill="none"
          />
          
          {/* Progress circle with natural colors */}
          <circle
            cx="50"
            cy="50"
            r="40"
            stroke="url(#energyGradient)"
            strokeWidth="6"
            fill="none"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-2000 ease-out"
          />
          
          {/* Natural gradient definition */}
          <defs>
            <linearGradient id="energyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#f97316'} />
              <stop offset="100%" stopColor={score >= 80 ? '#047857' : score >= 60 ? '#b45309' : '#c2410c'} />
            </linearGradient>
          </defs>
        </svg>

        {/* Center content */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className={`text-4xl font-bold bg-gradient-to-br ${getScoreColor(score)} bg-clip-text text-transparent`}>
              {score}%
            </div>
            <div className="text-sm text-gray-600 font-medium mt-1">
              Potentieel
            </div>
          </div>
        </div>

        {/* Subtle particle animation overlay */}
        <ParticleAnimation isActive={score > 0} score={score} />

        {/* Soft reflection effect */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/20 via-transparent to-transparent opacity-50"></div>
      </div>
    </div>
  );
};
